import * as React from 'react'

import moment from 'moment'

import { NightWorkActivityModel } from '@/app/models/nightWorkActivityModel'
import { BaseEventModel } from '@/app/models/baseEventModel'
import { FaIcon, faMoonSolid } from '@/lib/fa'

import { useScene } from '../context/SceneContext'

export type NightWorkDayBlockProps = {
  nightWorkActivity: NightWorkActivityModel
}

const boundaryColor = '#FFFFFF'
const CHIP_WIDTH = 20
const CHIP_HEIGHT = 12
// The day badges start this far below the axis line; the chip may dip that much.
const AXIS_OVERLAP = 4

type MarkerProps = {
  left: number
  chartWidth: number
  bottom: number
  begin: boolean
  tooltip: string
}

// Straddles the axis line, above the band the day badges use, so it never covers them.
const Marker: React.FC<MarkerProps> = ({
  left,
  chartWidth,
  bottom,
  begin,
  tooltip,
}) => {
  const afterBoundary = begin
    ? left + CHIP_WIDTH <= chartWidth
    : left - CHIP_WIDTH < 0

  return (
    <div
      className="tw-flex tw-flex-col tw-items-start tw-justify-end"
      style={{
        position: 'absolute',
        left,
        top: 0,
        bottom: bottom - AXIS_OVERLAP,
      }}
    >
      <div
        className="tw-flex tw-items-center tw-justify-center tw-gap-0.5 tw-rounded-sm tw-bg-[#1F2937]/95 tw-text-white"
        data-tip={tooltip}
        data-for="eventTooltip"
        style={{
          width: CHIP_WIDTH,
          height: CHIP_HEIGHT,
          pointerEvents: 'all',
          transform: afterBoundary ? undefined : 'translateX(-100%)',
          // The bright edge is the boundary itself: |> opens the period, >| closes it.
          [afterBoundary ? 'borderLeft' : 'borderRight']:
            `2px solid ${boundaryColor}`,
        }}
      >
        <FaIcon icon={faMoonSolid} className="tw-h-2 tw-w-2" />
        <svg width={4} height={7} style={{ display: 'block' }}>
          <path d="M0 0 L4 3.5 L0 7 Z" style={{ fill: '#FFFFFF' }} />
        </svg>
      </div>
    </div>
  )
}

export const NightWorkDayBlock: React.FC<NightWorkDayBlockProps> = React.memo(
  ({ nightWorkActivity }: NightWorkDayBlockProps) => {
    const scene = useScene()

    const start = moment(nightWorkActivity.startTime)
    const end = moment(nightWorkActivity.endTime)
    const viewStart = scene.startTime
    const viewEnd = scene.startTime.clone().add(scene.days, 'days')

    const info: BaseEventModel = {
      startTime: nightWorkActivity.startTime,
      endTime: nightWorkActivity.endTime,
      typeName: 'eventName.nightWorkActivity',
      color: '#444',
      hideDuration: true,
    }
    const tooltip = JSON.stringify(info)

    return (
      <div>
        {start.isSameOrAfter(viewStart) && start.isBefore(viewEnd) && (
          <Marker
            left={scene.timeToX(start)}
            chartWidth={scene.width}
            bottom={scene.bottomPadding}
            begin
            tooltip={tooltip}
          />
        )}
        {end.isAfter(viewStart) && end.isSameOrBefore(viewEnd) && (
          <Marker
            left={scene.timeToX(end)}
            chartWidth={scene.width}
            bottom={scene.bottomPadding}
            begin={false}
            tooltip={tooltip}
          />
        )}
      </div>
    )
  },
)

NightWorkDayBlock.displayName = 'NightWorkDayBlock'
